import {
  CheckCircleFilled,
  CreditCardOutlined,
  DeploymentUnitOutlined,
  ExclamationCircleFilled,
  KeyOutlined,
  LineChartOutlined,
} from '@ant-design/icons';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, api } from '../api/client';
import type { components } from '../api/schema.gen';
import { ActivityHeatmap } from '../components/ActivityHeatmap';
import { FreeQuotaCard } from '../components/FreeQuotaCard';
import { EmptyState } from '../components/EmptyState';
import { OnboardingNotice } from '../components/OnboardingNotice';
import { PageLoader } from '../components/PageLoader';
import { RequestTopUpModal } from '../components/RequestTopUpModal';
import { useLanguage } from '../lib/i18n/LanguageContext';
import { formatDateTime } from '../lib/dateFormat';
import { formatTHB, formatUsageCost, isDepleted } from '../lib/money';
import { fadeUp, staggerContainer } from '../lib/motionPresets';
import { useSession } from '../lib/SessionContext';

type UsageEvent = components['schemas']['UsageEvent'];

/** First two letters of the customer's name — or the email, if no display name is set. */
function initials(nameOrEmail: string): string {
  const [first, second] = nameOrEmail.trim().split(/\s+/).filter(Boolean);
  if (first && second) return (first.charAt(0) + second.charAt(0)).toUpperCase();
  return nameOrEmail.trim().slice(0, 2).toUpperCase();
}

function greetingKey(hour: number): 'greetingMorning' | 'greetingAfternoon' | 'greetingEvening' {
  if (hour < 12) return 'greetingMorning';
  if (hour < 18) return 'greetingAfternoon';
  return 'greetingEvening';
}

export function Dashboard() {
  const {
    ready,
    hasOrg,
    balance,
    me,
    avatarUrl,
    canManageBilling,
    isB2BOrg,
    depleted: orgDepleted,
    everFunded,
  } = useSession();
  const { dict } = useLanguage();
  const [usage, setUsage] = useState<UsageEvent[] | null>(null);
  const [usageError, setUsageError] = useState<string | null>(null);
  const [onboarding, setOnboarding] = useState(false);
  const [requestState, setRequestState] = useState<'idle' | 'sent' | 'cooldown' | 'error'>('idle');
  // One modal, not two buttons: "everyone" is the default choice inside it,
  // not a separate faster path — see the LLMR-55 follow-up discussion for
  // why this replaced the earlier two-button layout.
  const [requestModalOpen, setRequestModalOpen] = useState(false);

  const quickLinks = [
    {
      to: '/keys',
      title: dict.dashboard.quickLinks.keysTitle,
      body: dict.dashboard.quickLinks.keysBody,
      icon: <KeyOutlined aria-hidden="true" />,
    },
    {
      to: '/usage',
      title: dict.dashboard.quickLinks.usageTitle,
      body: dict.dashboard.quickLinks.usageBody,
      icon: <LineChartOutlined aria-hidden="true" />,
    },
    {
      to: '/models',
      title: dict.dashboard.quickLinks.modelsTitle,
      body: dict.dashboard.quickLinks.modelsBody,
      icon: <DeploymentUnitOutlined aria-hidden="true" />,
    },
    // Top-up is admin/owner only (LLMR-55) — a plain member has nowhere to
    // land after clicking this, so the shortcut itself is not shown to them.
    ...(canManageBilling
      ? [
          {
            to: '/topup',
            title: dict.dashboard.quickLinks.topupTitle,
            body: dict.dashboard.quickLinks.topupBody,
            icon: <CreditCardOutlined aria-hidden="true" />,
          },
        ]
      : []),
  ];

  useEffect(() => {
    if (!ready) return;
    if (!hasOrg) {
      setOnboarding(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const u = await api.usage({ limit: 8 });
        if (!cancelled) setUsage(u.data);
      } catch (e) {
        if (cancelled) return;
        if (e instanceof ApiError && e.status === 403) setOnboarding(true);
        else setUsageError(e instanceof ApiError ? (e.problem.detail ?? e.problem.title) : dict.dashboard.loadError);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, hasOrg, dict]);

  const depleted = balance !== null && isDepleted(balance);
  // ever_funded from GET /balance/status: only the "used up" copy when the
  // org previously held credit. A brand-new wallet (or status still unknown)
  // gets the gentler "no credit available" wording instead.
  const spentThrough = everFunded === true;
  const displayName = me ? me.display_name || me.email.split('@')[0] || me.email : '';
  const greet = dict.dashboard[greetingKey(new Date().getHours())];

  return (
    <div className="page overview">
      {onboarding ? (
        <>
          <h1 className="visually-hidden">{dict.common.dashboard}</h1>
          <OnboardingNotice area={dict.onboarding.dashboardArea} />
        </>
      ) : (
        <motion.div className="overview-body" variants={staggerContainer} initial="hidden" animate="show">
          {me ? (
            <motion.section variants={fadeUp} className="overview-welcome">
              {avatarUrl ? (
                <img className="profile-avatar profile-avatar--img" src={avatarUrl} alt="" />
              ) : (
                <span className="profile-avatar" aria-hidden="true">
                  {initials(me.display_name || me.email)}
                </span>
              )}
              <div className="profile-identity">
                <p className="overview-page-label">{dict.common.dashboard}</p>
                <h1 className="overview-kicker">{greet(displayName)}</h1>
                <div className="profile-email">{me.email}</div>
              </div>
            </motion.section>
          ) : (
            <h1 className="visually-hidden">{dict.common.dashboard}</h1>
          )}

          <motion.section variants={fadeUp} className="panel panel--hero overview-hero">
            <div className="overview-hero-balance">
              <p className="overview-balance-label">{dict.dashboard.creditBalance}</p>
              <div className="overview-balance-row">
                {canManageBilling ? (
                  <>
                    <div className={`balance${depleted ? ' depleted' : ''}`}>
                      {balance === null ? <PageLoader compact /> : formatTHB(balance)}
                    </div>
                    <Link to="/topup" className="btn-link">
                      {dict.dashboard.topUpCta}
                    </Link>
                  </>
                ) : orgDepleted === null ? (
                  // LLMR-55: a plain member never sees the org balance, but
                  // DOES learn whether it's depleted (GET /balance/status —
                  // no amount in the response, by construction) so a blocked
                  // API call has an explanation instead of a silent gap.
                  // Brief loading fallback before the first status resolves.
                  <p className="note">{dict.dashboard.balanceRestricted(isB2BOrg)}</p>
                ) : orgDepleted ? (
                  <span className="credit-status credit-status--depleted">
                    <ExclamationCircleFilled aria-hidden="true" />
                    {spentThrough
                      ? dict.dashboard.memberCreditDepleted
                      : dict.dashboard.memberCreditUnfunded}
                  </span>
                ) : (
                  <span className="credit-status credit-status--ok">
                    <CheckCircleFilled aria-hidden="true" />
                    {dict.dashboard.memberCreditSufficient}
                  </span>
                )}
              </div>
              {canManageBilling && depleted ? (
                <p className="sub warn-text">
                  {spentThrough ? dict.dashboard.depleted : dict.dashboard.unfunded}
                </p>
              ) : canManageBilling ? (
                <p className="note overview-prepaid-note">{dict.dashboard.prepaidNote}</p>
              ) : orgDepleted ? (
                <>
                  <p className="note warn-text">
                    {spentThrough
                      ? dict.dashboard.memberCreditDepletedContact
                      : dict.dashboard.memberCreditUnfundedContact}
                  </p>
                  {requestState === 'sent' ? (
                    <p className="note">{dict.dashboard.requestTopUpSent}</p>
                  ) : requestState === 'cooldown' ? (
                    <p className="note">{dict.dashboard.requestTopUpCooldown}</p>
                  ) : (
                    <button type="button" className="btn-link" onClick={() => setRequestModalOpen(true)}>
                      {dict.dashboard.requestTopUpCta}
                    </button>
                  )}
                  {requestState === 'error' && <p className="note warn-text">{dict.dashboard.requestTopUpError}</p>}
                </>
              ) : null}
            </div>
          </motion.section>

          <motion.section variants={fadeUp}>
            <FreeQuotaCard variant="compact" />
          </motion.section>

          <motion.section variants={fadeUp} className="overview-shortcuts" aria-label={dict.dashboard.quickLinks.heading}>
            <div className="quick-links">
              {quickLinks.map((q) => (
                <Link key={q.to} to={q.to} className="quick-link-card">
                  <span className="quick-link-icon">{q.icon}</span>
                  <div className="quick-link-copy">
                    <h3>{q.title}</h3>
                    <p>{q.body}</p>
                  </div>
                </Link>
              ))}
            </div>
          </motion.section>

          <motion.section variants={fadeUp}>
            <ActivityHeatmap ready={ready} hasOrg={hasOrg} />
          </motion.section>

          <motion.section variants={fadeUp} className="panel overview-usage">
            <div className="panel-head">
              <h2>{dict.dashboard.recentUsage}</h2>
              <Link to="/usage" className="panel-head-link">
                {dict.dashboard.viewAll}
              </Link>
            </div>

            {usageError && <div className="err">{usageError}</div>}

            {usage === null ? (
              <PageLoader />
            ) : (
            <div className="table-scroll">
              <table role="table">
                <thead role="rowgroup">
                  <tr role="row">
                    <th role="columnheader">{dict.dashboard.colWhen}</th>
                    <th role="columnheader">{dict.dashboard.colModel}</th>
                    <th role="columnheader" className="num">
                      {dict.dashboard.colIn}
                    </th>
                    <th role="columnheader" className="num">
                      {dict.dashboard.colOut}
                    </th>
                    <th role="columnheader" className="num">
                      {dict.dashboard.colCost}
                    </th>
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  {usage.length === 0 && (
                    <tr role="row">
                      <td role="cell" colSpan={5}>
                        <EmptyState title={dict.dashboard.emptyTitle}>
                          <p className="note">{dict.dashboard.emptyBody}</p>
                          <Link to="/keys" className="btn-link btn-link--inline">
                            {dict.dashboard.emptyCta}
                          </Link>
                        </EmptyState>
                      </td>
                    </tr>
                  )}
                  {usage.map((e) => (
                    // An interrupted stream is still billed for the tokens produced,
                    // so it is marked rather than hidden — a customer seeing a charge
                    // they cannot explain is worse than one they can.
                    <tr role="row" key={e.event_id} className={e.status ?? ''}>
                      <td role="cell" data-label={dict.dashboard.colWhen}>
                        {formatDateTime(e.occurred_at)}
                      </td>
                      <td role="cell" data-label={dict.dashboard.colModel}>
                        <span className="overview-model-cell">
                          {e.model_id}
                          {e.status === 'interrupted' && (
                            <span className="badge badge--interrupted">{dict.dashboard.statusInterrupted}</span>
                          )}
                          {e.status === 'failed' && (
                            <span className="badge badge--failed">{dict.dashboard.statusFailed}</span>
                          )}
                        </span>
                      </td>
                      <td role="cell" className="num" data-label={dict.dashboard.colIn}>
                        {e.tokens.input.toLocaleString()}
                      </td>
                      <td role="cell" className="num" data-label={dict.dashboard.colOut}>
                        {e.tokens.output.toLocaleString()}
                      </td>
                      <td role="cell" className="num" data-label={dict.dashboard.colCost}>
                        {formatUsageCost(e.cost.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </motion.section>
        </motion.div>
      )}

      <AnimatePresence>
        {requestModalOpen && (
          <RequestTopUpModal
            reason="balance"
            title={dict.dashboard.requestTopUpCta}
            cta={dict.dashboard.requestTopUpSendTo}
            onClose={() => setRequestModalOpen(false)}
            onResult={setRequestState}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
